import { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Checkbox,
    Skeleton,
    IconButton,
    Chip,
    Tooltip,
} from '@mui/material';
import { Add, Close, Check, ContentCopy } from '@mui/icons-material';
import RequestQuoteModal from './RequestQuoteModal';
import ClearBoxConfigReviewModal from './ClearBoxConfigReviewModal';
import {
    shouldShowQuestion,
    calculateFleetPriceBreakdown,
    formatPrice,
} from './clearBoxConfigUtils';

function LoadingSkeleton() {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                maxHeight: '100%',
                overflow: 'hidden',
                backgroundColor: 'rgba(255,255,255,0.9)',
            }}
        >
            {/* Header skeleton */}
            <Box
                sx={{
                    px: { xs: 2, md: 3 },
                    py: 2,
                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Skeleton variant="rectangular" width={32} height={32} sx={{ borderRadius: 1 }} />
                    <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="60%" height={24} />
                        <Skeleton variant="text" width="80%" height={18} sx={{ mt: 0.5 }} />
                    </Box>
                </Box>
            </Box>

            {/* Body skeleton */}
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    px: { xs: 2, md: 3 },
                    py: { xs: 2, md: 2.5 },
                }}
            >
                <Skeleton variant="text" width={180} height={20} sx={{ mb: 1.5 }} />
                
                {/* Question card skeletons */}
                {[1, 2, 3, 4].map((i) => (
                    <Box
                        key={i}
                        sx={{
                            border: '1px solid rgba(0,0,0,0.04)',
                            backgroundColor: '#fff',
                            p: 1.5,
                            mb: 2,
                        }}
                    >
                        <Skeleton variant="text" width="40%" height={22} sx={{ mb: 1 }} />
                        <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
                        <Skeleton variant="text" width="70%" height={14} sx={{ mt: 1 }} />
                    </Box>
                ))}
            </Box>

            {/* Footer skeleton */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    px: { xs: 2, md: 3 },
                    py: 1.5,
                    borderTop: '1px solid rgba(0,0,0,0.06)',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    flexShrink: 0,
                }}
            >
                <Skeleton variant="rectangular" width={130} height={32} sx={{ borderRadius: '999px' }} />
            </Box>
        </Box>
    );
}

function ErrorMessage({ error, onRetry }) {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                minHeight: 300,
                p: 3,
                textAlign: 'center',
                backgroundColor: 'rgba(255,255,255,0.9)',
            }}
        >
            <Box
                sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(211, 47, 47, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                }}
            >
                <Typography sx={{ fontSize: 28, color: '#d32f2f' }}>!</Typography>
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>
                Failed to load configuration
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 2, maxWidth: 300 }}>
                {error || 'Something went wrong while loading the questionnaire. Please try again.'}
            </Typography>
            {onRetry && (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={onRetry}
                    sx={{
                        borderRadius: '999px',
                        textTransform: 'none',
                        px: 3,
                    }}
                >
                    Try again
                </Button>
            )}
        </Box>
    );
}

export default function ClearBoxConfigPanel({
    questionnaire,
    machines = [],
    activeMachineIndex = 0,
    onActiveMachineChange,
    onAnswerChange,
    onAddMachine,
    onRemoveMachine,
    onRenameMachine,
    onDuplicateMachine,
    maxMachines = 10,
    isLoading = false,
    error = null,
    onRetry = null,
}) {
    const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
    const [isReviewOpen, setIsReviewOpen] = useState(false);
    const [editingMachineIndex, setEditingMachineIndex] = useState(null);
    const [editingName, setEditingName] = useState('');

    const activeMachine = useMemo(() => machines[activeMachineIndex] || machines[0] || { answers: {} }, [machines, activeMachineIndex]);
    const answers = useMemo(() => activeMachine.answers || {}, [activeMachine]);

    const steps = questionnaire?.steps ?? [];

    // Section definitions for grouping questions
    const sectionDefinitions = [
        { id: 'machine-info', title: 'Machine Information' },
        { id: 'scanner-config', title: 'Scanner Configuration' },
        { id: 'monitoring-config', title: 'Monitoring Configuration' },
    ];

    // Group questions by section while preserving order
    const getQuestionsBySection = () => {
        const grouped = {};
        sectionDefinitions.forEach((sec) => {
            grouped[sec.id] = [];
        });
        grouped['other'] = []; // fallback for questions without section

        steps.forEach((question) => {
            const sectionId = question.section || 'other';
            if (grouped[sectionId]) {
                grouped[sectionId].push(question);
            } else {
                grouped['other'].push(question);
            }
        });

        return grouped;
    };

    const questionsBySection = getQuestionsBySection();

    // Pricing calculation — summed across all machines
    const basePrice = questionnaire?.basePrice ?? 9800;
    const additionalLaserPrice = questionnaire?.additionalLaserPrice ?? 4300;

    const priceBreakdown = calculateFleetPriceBreakdown(machines, basePrice, additionalLaserPrice);

    // Auto-set lasers-to-monitor to 1 when laser-count is 1
    useEffect(() => {
        const laserCount = answers ? answers['laser-count'] : undefined;
        if ((laserCount === 1 || laserCount === '1') && answers && answers['lasers-to-monitor'] !== 1) {
            onAnswerChange && onAnswerChange('lasers-to-monitor', 1);
        }
    }, [answers, onAnswerChange]);

    // Show error message if loading failed
    if (error) {
        return <ErrorMessage error={error} onRetry={onRetry} />;
    }

    // Show loading skeleton while data is being fetched
    if (isLoading) {
        return <LoadingSkeleton />;
    }

    // Show error if questionnaire failed to load (no data after loading)
    if (!questionnaire) {
        return <ErrorMessage error="No configuration data available" onRetry={onRetry} />;
    }

    const getAnswer = (id) => (answers && Object.prototype.hasOwnProperty.call(answers, id) ? answers[id] : '');

    const handleTextChange = (questionId, event) => {
        onAnswerChange && onAnswerChange(questionId, event.target.value);
    };

    const handleNumberChange = (questionId, event) => {
        const raw = event.target.value;
        if (raw === '') {
            onAnswerChange && onAnswerChange(questionId, '');
            return;
        }
        // Parse as integer only
        const numeric = parseInt(raw, 10);
        if (Number.isNaN(numeric)) {
            return;
        }
        
        let finalValue = numeric;
        
        // For lasers-to-monitor, clamp to laser-count
        if (questionId === 'lasers-to-monitor') {
            const laserCount = answers?.['laser-count'];
            if (laserCount && numeric > laserCount) {
                finalValue = laserCount;
            }
        }
        
        onAnswerChange && onAnswerChange(questionId, finalValue);
    };

    const handleSingleChoiceChange = (questionId, event) => {
        onAnswerChange && onAnswerChange(questionId, event.target.value);
    };

    const handleMultiChoiceToggle = (questionId, optionId) => {
        const current = (answers && Array.isArray(answers[questionId])) ? answers[questionId] : [];
        const exists = current.includes(optionId);
        const next = exists ? current.filter((id) => id !== optionId) : [...current, optionId];
        onAnswerChange && onAnswerChange(questionId, next);
    };

    return (
        <Box
            className="clearbox-config-panel"
            sx={{
                boxShadow: 'none',
                backgroundColor: 'rgba(255,255,255,0.9)',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                maxHeight: '100%',
                overflow: 'hidden',
            }}
        >
            <ClearBoxConfigReviewModal
                open={isReviewOpen}
                onClose={() => setIsReviewOpen(false)}
                questionnaire={questionnaire}
                machines={machines}
                basePrice={basePrice}
                additionalLaserPrice={additionalLaserPrice}
                onEditMachine={(idx) => {
                    onActiveMachineChange(idx);
                    setIsReviewOpen(false);
                }}
                onContinue={() => {
                    setIsReviewOpen(false);
                    setIsQuoteModalOpen(true);
                }}
            />
            <RequestQuoteModal
                open={isQuoteModalOpen}
                onClose={() => setIsQuoteModalOpen(false)}
                questionnaire={questionnaire}
                machines={machines}
                answers={answers}
                onAnswerChange={onAnswerChange}
            />
            {/* Top header with title and brief description */}
            <Box
                className="clearbox-config-panel__header"
                sx={{
                    px: { xs: 2, md: 3 },
                    py: 2,
                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    flexShrink: 0,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                    }}
                >
                    <Box
                        component="img"
                        src={`${process.env.PUBLIC_URL}/static/images/ClearBox/clearbox.svg`}
                        alt="ClearBox logo"
                        sx={{
                            height: 32,
                            width: 'auto',
                            flexShrink: 0,
                        }}
                    />
                    <Box sx={{ textAlign: 'left' }}>
                        <Typography
                            variant="subtitle2"
                            sx={{ color: '#333', fontWeight: 600 }}
                        >
                            {questionnaire?.title || 'ClearBox configuration questionnaire'}
                        </Typography>
                        {questionnaire?.description && (
                            <Typography
                                variant="body2"
                                sx={{ color: '#555', mt: 0.5 }}
                            >
                                {questionnaire.description}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Machine selector bar */}
            <Box
                sx={{
                    px: { xs: 2, md: 3 },
                    py: 1.5,
                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                    backgroundColor: 'rgba(248,250,252,0.95)',
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {machines.map((machine, idx) => (
                    <Box key={machine.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <Chip
                            label={
                                editingMachineIndex === idx ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <TextField
                                            size="small"
                                            variant="standard"
                                            value={editingName}
                                            onChange={(e) => setEditingName(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    onRenameMachine(idx, editingName || `Machine ${idx + 1}`);
                                                    setEditingMachineIndex(null);
                                                }
                                                if (e.key === 'Escape') {
                                                    setEditingMachineIndex(null);
                                                }
                                            }}
                                            autoFocus
                                            inputProps={{ style: { fontSize: '0.75rem', padding: '2px 0', width: 80 } }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                onRenameMachine(idx, editingName || `Machine ${idx + 1}`);
                                                setEditingMachineIndex(null);
                                            }}
                                            sx={{ p: 0.25 }}
                                        >
                                            <Check sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Box>
                                ) : machine.name
                            }
                            onClick={() => onActiveMachineChange(idx)}
                            onDoubleClick={() => {
                                setEditingMachineIndex(idx);
                                setEditingName(machine.name);
                            }}
                            onDelete={machines.length > 1 ? () => onRemoveMachine(idx) : undefined}
                            deleteIcon={machines.length > 1 ? <Close sx={{ fontSize: 14 }} /> : undefined}
                            variant={idx === activeMachineIndex ? 'filled' : 'outlined'}
                            size="small"
                            sx={{
                                fontWeight: idx === activeMachineIndex ? 600 : 400,
                                backgroundColor: idx === activeMachineIndex ? 'rgba(108,152,173,0.15)' : 'transparent',
                                borderColor: idx === activeMachineIndex ? '#6C98AD' : 'rgba(0,0,0,0.2)',
                                color: idx === activeMachineIndex ? '#3a6b80' : '#555',
                                '&:hover': { backgroundColor: 'rgba(108,152,173,0.1)' },
                                cursor: editingMachineIndex === idx ? 'default' : 'pointer',
                            }}
                        />
                        {machines.length < maxMachines && (
                            <Tooltip title="Duplicate machine">
                                <IconButton
                                    size="small"
                                    onClick={() => onDuplicateMachine(idx)}
                                    sx={{ p: 0.25, color: '#6C98AD' }}
                                >
                                    <ContentCopy sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                    ))}
                    {machines.length < maxMachines && (
                        <Tooltip title="Add machine">
                            <IconButton
                                size="small"
                                onClick={onAddMachine}
                                sx={{
                                    border: '1px dashed rgba(108,152,173,0.5)',
                                    borderRadius: '16px',
                                    px: 1,
                                    py: 0.25,
                                    color: '#6C98AD',
                                    '&:hover': { backgroundColor: 'rgba(108,152,173,0.08)' },
                                }}
                            >
                                <Add sx={{ fontSize: 16 }} />
                                <Typography variant="caption" sx={{ fontSize: '0.7rem', ml: 0.25 }}>Add</Typography>
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
                {machines.length > 1 && (
                    <Typography variant="caption" sx={{ color: '#999', fontSize: '0.65rem', mt: 0.5, display: 'block' }}>
                        Double-click a machine name to rename it. Configuring: {activeMachine.name}
                    </Typography>
                )}
            </Box>

            <Box
                className="clearbox-config-panel__body"
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    px: { xs: 2, md: 3 },
                    py: { xs: 2, md: 2.5 },
                    gap: 3,
                }}
            >
            {/* Questionnaire sections */}
            {sectionDefinitions.map((section) => {
                const sectionQuestions = questionsBySection[section.id] || [];
                const visibleQuestions = sectionQuestions.filter((question) => shouldShowQuestion(question, answers));
                
                // Don't render empty sections
                if (visibleQuestions.length === 0) {
                    return null;
                }

                return (
                    <Box 
                        key={section.id} 
                        sx={{ 
                            textAlign: 'left',
                            pb: 2.5,
                            borderBottom: '1px solid rgba(0,0,0,0.08)',
                            '&:last-of-type': {
                                borderBottom: 'none',
                                pb: 0,
                            },
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            sx={{
                                textTransform: 'uppercase',
                                fontWeight: 600,
                                mb: 1.5,
                                color: 'var(--secondary-color, #6C98AD)',
                                letterSpacing: '0.5px',
                            }}
                        >
                            {section.title}
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {visibleQuestions.map((question) => {
                                const value = getAnswer(question.id);

                        return (
                            <Box
                                key={question.id}
                                className="clearbox-config-panel__question-card"
                                sx={{
                                    border: '1px solid rgba(0,0,0,0.04)',
                                    backgroundColor: '#fff',
                                    p: 1.5,
                                }}
                            >
                                <FormControl component="fieldset" sx={{ width: '100%' }}>
                                    <FormLabel component="legend" sx={{ fontWeight: 600, color: '#222' }}>
                                        {question.title}
                                        {question.required && (
                                            <Typography
                                                component="span"
                                                sx={{ color: '#d32f2f', ml: 0.5, fontWeight: 700 }}
                                            >
                                                *
                                            </Typography>
                                        )}
                                    </FormLabel>

                                    {question.type === 'text' && (
                                        <TextField
                                            fullWidth
                                            size="small"
                                            sx={{ mt: 1.5 }}
                                            placeholder={question.placeholder || ''}
                                            value={typeof value === 'string' ? value : ''}
                                            onChange={(e) => handleTextChange(question.id, e)}
                                        />
                                    )}

                                    {question.type === 'number' && (
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            sx={{ mt: 1.5 }}
                                            placeholder={question.placeholder || ''}
                                            inputProps={{
                                                min: question.min ?? 0,
                                                max: question.id === 'lasers-to-monitor' ? (answers?.['laser-count'] || undefined) : undefined,
                                                step: 1,
                                            }}
                                            value={value === '' || value === undefined || value === null ? '' : value}
                                            onChange={(e) => handleNumberChange(question.id, e)}
                                        />
                                    )}

                                    {(question.type === 'single-choice' || question.type === 'single-select') && (
                                        <RadioGroup
                                            sx={{ mt: 1 }}
                                            value={typeof value === 'string' ? value : ''}
                                            onChange={(e) => handleSingleChoiceChange(question.id, e)}
                                        >
                                            {question.options && question.options.map((opt) => (
                                                <FormControlLabel
                                                    key={opt.id}
                                                    value={opt.id}
                                                    control={<Radio size="small" />}
                                                    label={opt.label}
                                                />
                                            ))}
                                        </RadioGroup>
                                    )}

                                    {(question.type === 'multi-choice' || question.type === 'multi-select') && (
                                        <Box sx={{ mt: 1 }}>
                                            {question.options && question.options.map((opt) => {
                                                const current = Array.isArray(value) ? value : [];
                                                const checked = current.includes(opt.id);
                                                return (
                                                    <Box key={opt.id} sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={checked}
                                                                    onChange={() => handleMultiChoiceToggle(question.id, opt.id)}
                                                                />
                                                            }
                                                            label={opt.label}
                                                        />
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    )}

                                    {question.disclaimer && (
                                        <Typography
                                            variant="caption"
                                            sx={{ color: '#b26a00', mt: 1, display: 'block' }}
                                        >
                                            {question.disclaimer}
                                        </Typography>
                                    )}

                                    {question.helpText && (
                                        <Typography
                                            variant="caption"
                                            sx={{ color: '#777', mt: question.disclaimer ? 0.5 : 1, display: 'block' }}
                                        >
                                            {question.helpText}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Box>
                        );
                    })}
                        </Box>
                    </Box>
                );
            })}

            {/* Fallback: Questions without a section */}
            {(questionsBySection['other'] || []).filter((question) => shouldShowQuestion(question, answers)).length > 0 && (
                <Box 
                    sx={{ 
                        textAlign: 'left',
                        pb: 2.5,
                        borderBottom: '1px solid rgba(0,0,0,0.08)',
                    }}
                >
                    <Typography
                        variant="subtitle2"
                        sx={{
                            textTransform: 'uppercase',
                            fontWeight: 600,
                            mb: 1.5,
                            color: 'var(--secondary-color, #6C98AD)',
                            letterSpacing: '0.5px',
                        }}
                    >
                        Other
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {(questionsBySection['other'] || []).filter((question) => shouldShowQuestion(question, answers)).map((question) => {
                            const value = getAnswer(question.id);
                            return (
                                <Box
                                    key={question.id}
                                    className="clearbox-config-panel__question-card"
                                    sx={{
                                        border: '1px solid rgba(0,0,0,0.04)',
                                        backgroundColor: '#fff',
                                        p: 1.5,
                                    }}
                                >
                                    <FormControl component="fieldset" sx={{ width: '100%' }}>
                                        <FormLabel component="legend" sx={{ fontWeight: 600, color: '#222' }}>
                                            {question.title}
                                            {question.required && (
                                                <Typography
                                                    component="span"
                                                    sx={{ color: '#d32f2f', ml: 0.5, fontWeight: 700 }}
                                                >
                                                    *
                                                </Typography>
                                            )}
                                        </FormLabel>
                                        {question.type === 'text' && (
                                            <TextField
                                                fullWidth
                                                size="small"
                                                sx={{ mt: 1.5 }}
                                                placeholder={question.placeholder || ''}
                                                value={typeof value === 'string' ? value : ''}
                                                onChange={(e) => handleTextChange(question.id, e)}
                                            />
                                        )}
                                        {question.type === 'number' && (
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                sx={{ mt: 1.5 }}
                                                placeholder={question.placeholder || ''}
                                                inputProps={{
                                                    min: question.min ?? 0,
                                                    max: question.id === 'lasers-to-monitor' ? (answers?.['laser-count'] || undefined) : undefined,
                                                    step: 1,
                                                }}
                                                value={value === '' || value === undefined || value === null ? '' : value}
                                                onChange={(e) => handleNumberChange(question.id, e)}
                                            />
                                        )}
                                        {question.helpText && (
                                            <Typography
                                                variant="caption"
                                                sx={{ color: '#777', mt: 1, display: 'block' }}
                                            >
                                                {question.helpText}
                                            </Typography>
                                        )}
                                    </FormControl>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            )}
            </Box>
            <Box
                className="clearbox-config-panel__footer"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    px: { xs: 2, md: 3 },
                    py: 1.5,
                    borderTop: '1px solid rgba(0,0,0,0.06)',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    flexShrink: 0,
                }}
            >
                {/* Pricing and buttons row */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: { xs: 1.5, sm: 2 },
                        width: '100%',
                    }}
                >
                    {/* Pricing display */}
                    <Box>
                        {/* Section heading */}
                        <Typography
                            variant="subtitle2"
                            sx={{
                                textTransform: 'uppercase',
                                fontWeight: 600,
                                color: '#333',
                                mb: 1,
                            }}
                        >
                            System Pricing {machines.length > 1 ? `(${machines.length} machines)` : ''}
                        </Typography>

                        {/* Per-machine base price */}
                        <Typography
                            variant="caption"
                            sx={{
                                color: '#666',
                                display: 'flex',
                                justifyContent: 'space-between',
                                mb: 0.25,
                            }}
                        >
                            <span>Base System (1 Laser) {priceBreakdown.machineCount > 1 ? `× ${priceBreakdown.machineCount}` : ''}</span>
                            <span>{formatPrice(basePrice * (priceBreakdown.machineCount || 1))}</span>
                        </Typography>
                        
                        {/* Additional lasers line - show when there are additional lasers */}
                        {priceBreakdown.additionalLasers > 0 && (
                            <Typography
                                variant="caption"
                                sx={{
                                    color: '#666',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    mb: 0.25,
                                }}
                            >
                                <span>Additional Lasers (×{priceBreakdown.additionalLasers} total)</span>
                                <span>{formatPrice(priceBreakdown.additionalCost)}</span>
                            </Typography>
                        )}
                        
                        {/* Estimated total when breakdown available */}
                        {priceBreakdown.total !== null && (
                            <Box
                                sx={{
                                    borderTop: '1px solid rgba(0,0,0,0.1)',
                                    pt: 0.5,
                                    mt: 0.5,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 700,
                                        color: '#2e7d32',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        gap: 2,
                                    }}
                                >
                                    <span>Estimated Total</span>
                                    <span>{formatPrice(priceBreakdown.total)}</span>
                                </Typography>
                            </Box>
                        )}
                        
                        <Typography
                            variant="caption"
                            sx={{
                                color: '#999',
                                display: 'block',
                                fontSize: '0.7rem',
                                fontStyle: 'italic',
                                mt: priceBreakdown.total !== null ? 1 : 0.5,
                            }}
                        >
                            Prices exclude applicable taxes.
                        </Typography>
                    </Box>
                    {/* Buttons */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, flexShrink: 0 }}>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            href={`${process.env.PUBLIC_URL}/projects/clearbox-working-principle`}
                            sx={{
                                px: 2.5,
                                py: 0.6,
                                borderRadius: "999px",
                                fontWeight: 500,
                                fontSize: '0.8rem',
                                textTransform: "none",
                                borderWidth: 2,
                                borderColor: "rgba(108,152,173,0.7)",
                                color: "var(--secondary-color)",
                                "&:hover": {
                                    borderColor: "#6C98AD",
                                    backgroundColor: "rgba(108,152,173,0.06)",
                                },
                            }}
                        >
                            Learn More
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            onClick={() => setIsReviewOpen(true)}
                            sx={{
                                px: 2.5,
                                py: 0.6,
                                borderRadius: "999px",
                                fontWeight: 500,
                                fontSize: '0.8rem',
                                textTransform: "none",
                                borderWidth: 2,
                                borderColor: "rgba(108,152,173,0.7)",
                                color: "var(--secondary-color)",
                                "&:hover": {
                                    borderColor: "#6C98AD",
                                    backgroundColor: "rgba(108,152,173,0.06)",
                                },
                            }}
                        >
                            Request a quote
                        </Button>
                        </Box>
                        <Typography
                            variant="body2"
                            sx={{
                                opacity: 0.6,
                                fontSize: "0.75rem",
                                fontStyle: "italic",
                                textAlign: 'right',
                            }}
                        >
                            Please note the ClearBox has a lead time of 8 weeks
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
